import type {
  AIUsageLevel,
  EvidenceKind,
  ProofCard,
  ProofFreshness,
  ProofLevel,
} from "./types";

/**
 * 証明レベルの表示。設計書 12.1 の「表示例」列そのまま。
 *
 * 数字も「上級・中級」も出さない。出した瞬間に、見る側が人を並べる物差しとして
 * 使い始める。ここで言えるのは「何が確かめられたか」だけ。
 */
const LEVEL_LABEL: Record<ProofLevel, string> = {
  0: "自己申告",
  1: "学習済み",
  2: "テスト済み",
  3: "説明済み",
  4: "レビュー済み",
};

export function proofLevelLabel(level: number): string {
  const known = LEVEL_LABEL[level as ProofLevel];
  return known ?? LEVEL_LABEL[0];
}

/**
 * 自己申告と検証済みを視覚的に分ける。設計書 10.4 の原則。
 *
 * 自己申告 (L0) だけ枠線だけの見た目にして、塗りを与えない。
 * 同じ塗りにすると、本人が名前を足しただけのスキルが、
 * テストを通したスキルと同じ重さに見えてしまう。
 */
export function proofLevelStyle(level: number): string {
  switch (level) {
    case 4:
      return "bg-primary text-white border-transparent";
    case 3:
      return "bg-primary/15 text-primary border-primary/30";
    case 2:
      return "bg-primary/10 text-primary border-primary/20";
    case 1:
      return "bg-third text-sub border-main";
    default:
      return "bg-transparent text-placeholder border-dashed border-main";
  }
}

const EVIDENCE_LABEL: Record<EvidenceKind, string> = {
  self: "本人の申告",
  lesson: "レッスン修了",
  course: "コース修了",
  execution: "コード実行",
  test: "テスト通過",
  project: "作品での利用",
  explanation: "説明への回答",
  review: "講師レビュー",
};

export function evidenceKindLabel(kind: EvidenceKind): string {
  return EVIDENCE_LABEL[kind] ?? kind;
}

/**
 * 鮮度の注記。設計書 12.3。
 *
 * 「期限切れ」とは書かない。技能そのものが消えるわけではないので、
 * 断定すると本人にも見る側にも嘘になる。いつの記録かだけを添える。
 */
export function freshnessNote(freshness: ProofFreshness): string | null {
  switch (freshness) {
    case "aging":
      return "しばらく更新されていません";
    case "past":
      return "1年以上前の記録です";
    default:
      return null;
  }
}

const AI_USAGE_LABEL: Record<AIUsageLevel, string> = {
  none: "利用なし",
  assist: "アイデア・調査の補助",
  debug: "説明・デバッグの補助",
  partial: "一部のコード生成",
  major: "大部分のコード生成",
};

export function aiUsageLabel(level: AIUsageLevel): string {
  return AI_USAGE_LABEL[level] ?? AI_USAGE_LABEL.none;
}

/**
 * 証明カードの並び順。強い証明から先に出す。
 *
 * 同じレベルなら新しい方を先にする。古い証明が上に居座ると、
 * ページ全体が過去の記録に見える。
 */
export function sortProofCards(cards: ProofCard[]): ProofCard[] {
  return [...cards].sort((a, b) => {
    if (a.level !== b.level) return b.level - a.level;
    return b.lastEvidenceAt.localeCompare(a.lastEvidenceAt);
  });
}

/**
 * 採用担当者モードの 30 秒要約に載せるスキル。設計書 10.5。
 *
 * 自己申告 (L0) は落とす。ここは「確かめられたこと」を 30 秒で伝える面なので、
 * 本人が名前を足しただけのものを混ぜると、要約そのものが信用されなくなる。
 */
export function verifiedSkillNames(cards: ProofCard[]): string[] {
  return sortProofCards(cards)
    .filter((card) => card.level >= 1)
    .map((card) => card.skillName);
}
